"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import toast from "react-hot-toast";
import { googleAuth } from "@/store/slices/authSlice";
import { useAppDispatch, useAppSelector } from "@/store/store";

interface AuthPageProps {
  mode: "login" | "signup";
  logoUrl?: string;
  logoAlt?: string;
  redirectPath?: string;
  onEmailAuth?: () => void;
}

function GoogleAuthButton({
  mode,
}: {
  mode: "login" | "signup";
  redirectPath: string;
}) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const { isLoading } = useAppSelector((state) => state.auth);
  const invitationEmail = searchParams.get("email");
  const invitationToken = searchParams.get("invitation");
  const slug = searchParams.get("slug");

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const res = await dispatch(
          googleAuth({
            googleCode: tokenResponse.code,
            slug: slug || undefined,
          }),
        ).unwrap();

        const isLogin = mode === "login";
        toast.success(`${isLogin ? "Login" : "Sign up"} successful!`);

        if (
          invitationEmail &&
          res.user?.email &&
          invitationEmail === res.user.email
        ) {
          if (invitationToken) {
            router.push(`/invite/${invitationToken}/accept`);
            return;
          }
        }

        if (!res.user.organizations || res.user.organizations.length === 0) {
          router.push("/organization");
        } else if (!res.user.name) {
          router.push("/name");
        } else {
          if (res.user?.isWelcomeCompleted === false) {
            router.push("/welcome");
          } else {
            router.push("/dashboard/insight");
          }
        }
      } catch (error) {
        console.error(`Google ${mode} failed:`, error);
        toast.error(`Google ${mode} failed. Please try again.`);
      }
    },
    onError: (error) => {
      console.error("Google OAuth failed:", error);
    },
    flow: "auth-code",
  });

  return (
    <button
      onClick={() => handleGoogleLogin()}
      disabled={isLoading}
      className="w-full bg-accent-primary text-foreground font-medium border-[0.5px] border-accent-blue py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center space-x-2"
    >
      <Image
        src={"/assets/images/google.svg"}
        alt="google-icon"
        width={24}
        height={24}
      />
      <span>{isLoading ? "Loading..." : "Continue with Google"}</span>
    </button>
  );
}

function AuthPageContent({
  mode,
  logoUrl = "/assets/images/assetilo.svg",
  logoAlt = "logo",
  redirectPath = "/dashboard",
  onEmailAuth,
}: AuthPageProps) {
  const isLogin = mode === "login";
  const { error } = useAppSelector((state) => state.auth);
  const searchParams = useSearchParams();
  const slug = searchParams.get("slug");

  const getToggleText = () => {
    if (isLogin) {
      return (
        <>
          Don&apos;t have an account?{" "}
          <Link
            href={`/signup${slug ? `?slug=${slug}` : ""}`}
            className="text-white hover:text-gray-200 font-medium"
          >
            Sign Up
          </Link>
        </>
      );
    } else {
      return (
        <>
          Already have an account?{" "}
          <Link
            href={`/login${slug ? `?slug=${slug}` : ""}`}
            className="text-white hover:text-gray-200 font-medium"
          >
            Log In
          </Link>
        </>
      );
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-xs">
        <Link href="/" className="flex justify-center mb-10">
          <div className="relative w-10.5 h-9">
            <Image
              src={logoUrl}
              alt={logoAlt}
              fill
              className="object-contain"
            />
          </div>
        </Link>

        <div className="space-y-4">
          {onEmailAuth && (
            <button
              onClick={onEmailAuth}
              className="w-full bg-gunmetal text-foreground border-[0.5px] border-dark-slate cursor-pointer font-medium py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center space-x-2"
            >
              <span>Continue with Email</span>
            </button>
          )}
          <GoogleAuthButton mode={mode} redirectPath={redirectPath} />

          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <p className="text-sm text-gray-400 text-center mt-7">
            By {isLogin ? "logging in" : "signing up"}, you agree to our{" "}
            <Link href="#" className="text-white font-medium">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link href="#" className="text-white font-medium">
              Data Processing Agreement
            </Link>
            .
          </p>

          <p className="text-sm font-medium text-gray-400 text-center mt-7">
            {getToggleText()}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function AuthPage(props: AuthPageProps) {
  const isClient = typeof window !== "undefined";

  if (!isClient) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-xs">
          <div className="flex justify-center mb-10">
            <div className="w-10 h-10 bg-gray-600 rounded-lg" />
          </div>
          <div className="space-y-4">
            <div className="w-full h-12 bg-gray-700 rounded-lg" />
            <div className="w-full h-12 bg-gray-700 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID!}>
      <AuthPageContent {...props} />
    </GoogleOAuthProvider>
  );
}
